import { ApolloProvider } from "@apollo/client/react";
import { CssBaseline, GlobalStyles, StyledEngineProvider, ThemeProvider } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { getUserData, User } from "@shuttlerock/auth";
import { ClientAccountProvider, getUserClientAccount } from "@shuttlerock/client-account-context";
import type { ClientAccountContext } from "@shuttlerock/client-account-context";
import { ConfigService } from "@shuttlerock/configuration";
import { RouteErrorBoundary } from "@shuttlerock/error-handling";
import {
  FeatureGateProvider,
  registerFeatureGateForUser,
  type FeatureGate,
} from "@shuttlerock/feature-gates";
import {
  ToastProvider,
  SrTheme,
  THEME_MODE_STORAGE_KEY,
  THEME_SCHEME_STORAGE_KEY,
} from "@shuttlerock/mui-components";
import { useIdentifyLoggingUser } from "@shuttlerock/observability";
import { HtmlTitle } from "@shuttlerock/shell-components";
import {
  StorageApiUploadOptionsProvider,
  createStorageApiUploadOptions,
} from "@shuttlerock/uploading";
import { QueryParamProvider } from "@shuttlerock/use-query-params";
import { QueryClientProvider } from "@tanstack/react-query";
import { LDProvider } from "launchdarkly-react-client-sdk";
import { PropsWithChildren, ReactElement, useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { LoaderFunctionArgs, Outlet, useLoaderData } from "react-router";
import { AutoUpdate } from "./components/AutoUpdate/AutoUpdate";
import { routeErrorComponents } from "./components/ErrorPage/route-error-components";
import { HubSpotTracking } from "./components/HubSpot/HubSpotTracking";
import { gqlClient } from "./data/gqlClient";
import { queryClient } from "./data/queryClient";
import "@shuttlerock/i18n";
import { flags } from "./feature/flags";
import { GraphQLClient } from "./graphql/client";
import { useSyncHtmlLang } from "./i18n/useSyncHtmlLang";
import { isAuthRoute, shouldSkipClientAccountBootstrap } from "./routeBootstrap";

type LoaderResponse = {
  user: User | undefined;
  clientAccountContext: ClientAccountContext;
  featureGate: FeatureGate;
};

const EMPTY_CLIENT_ACCOUNT_CONTEXT: ClientAccountContext = {
  isMultiAccountUser: false,
  isMultiBrandUser: false,
};

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  if (isAuthRoute(url.pathname)) {
    const featureGate = registerFeatureGateForUser(undefined);
    return {
      user: undefined,
      clientAccountContext: EMPTY_CLIENT_ACCOUNT_CONTEXT,
      featureGate,
    };
  }

  const data = await getUserData(request);
  const user = data?.user;
  const featureGate = registerFeatureGateForUser(user);
  const clientAccountContext = shouldSkipClientAccountBootstrap(url.pathname)
    ? EMPTY_CLIENT_ACCOUNT_CONTEXT
    : await getUserClientAccount(gqlClient, user, featureGate);

  const response: LoaderResponse = { user, clientAccountContext, featureGate };

  return response;
}

function logAppVersion() {
  const version = ConfigService.APP_VERSION();

  console.info(`[Shuttlerock Cloud] version: ${version}`);
}

// MUI and Tailwind share one cascade (see index.css). Emotion emits into `@layer mui`
// under StyledEngineProvider; the statement here fixes the layer order before any
// Emotion style is injected, ahead of the stylesheet arriving.
const cssLayerOrder = <GlobalStyles styles="@layer theme, base, mui, components, utilities;" />;

// Set 100% height in throughout App/Shell to allow child apps to use 100% height.
const fullHeightStyle = { height: "100%" };
const heightStyles = (
  <GlobalStyles
    styles={{
      html: fullHeightStyle,
      body: fullHeightStyle,
      "#root": fullHeightStyle,
    }}
  />
);

export const Layout = ({ children }: PropsWithChildren): ReactElement => {
  const autoUpdateInterval = ConfigService.AUTO_UPDATE_INTERVAL() || false;

  return (
    <StyledEngineProvider enableCssLayer>
      {cssLayerOrder}
      <ThemeProvider
        theme={SrTheme}
        modeStorageKey={THEME_MODE_STORAGE_KEY}
        colorSchemeStorageKey={THEME_SCHEME_STORAGE_KEY}
        disableTransitionOnChange
        defaultMode="light"
      >
        <CssBaseline />
        {heightStyles}
        <ToastProvider>
          <HelmetProvider>
            <HtmlTitle>Shuttlerock</HtmlTitle>
            {children}
          </HelmetProvider>
          {typeof autoUpdateInterval === "number" ? (
            <AutoUpdate fetchInterval={autoUpdateInterval} />
          ) : null}
        </ToastProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

const storageApiOptions = createStorageApiUploadOptions({ gqlClient });

const Root = () => {
  const { user, clientAccountContext, featureGate } = useLoaderData() as LoaderResponse;
  useIdentifyLoggingUser(user);
  useEffect(() => logAppVersion(), []);
  useEffect(() => {
    void flags.identifyUser(user).catch((error) => {
      console.warn("[Shuttlerock Cloud] Failed to identify LaunchDarkly user context.", error);
    });
  }, [user]);
  useSyncHtmlLang();

  return (
    <Layout>
      <FeatureGateProvider gate={featureGate}>
        <QueryClientProvider client={queryClient}>
          <ApolloProvider client={GraphQLClient}>
            <LDProvider clientSideID="" ldClient={flags.client}>
              <QueryParamProvider>
                <ClientAccountProvider value={clientAccountContext}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <StorageApiUploadOptionsProvider value={storageApiOptions}>
                      <HubSpotTracking />
                      <Outlet />
                    </StorageApiUploadOptionsProvider>
                  </LocalizationProvider>
                </ClientAccountProvider>
              </QueryParamProvider>
            </LDProvider>
          </ApolloProvider>
        </QueryClientProvider>
      </FeatureGateProvider>
    </Layout>
  );
};

export default Root;

export const ErrorBoundary = () => {
  return (
    <Layout>
      <RouteErrorBoundary boundaryName="root" components={routeErrorComponents} />
    </Layout>
  );
};

export { Loading as HydrateFallback } from "./components/Loading/Loading";
